import React, { useState, useEffect, useRef } from 'react';
import './Profile.css';
import { withTranslation } from 'react-i18next';
import Header from "../Header/Header.jsx";
import { Button, Form, FormControl, InputGroup, Jumbotron, Tab, Tabs } from "react-bootstrap";
import ToastComponent from '../Toast/Toast';
import SignatureCanvas from 'react-signature-canvas';
import { Redirect } from "react-router-dom";
import { PulseLoader } from "react-spinners";
import { MdClose } from "react-icons/md";
import ThemeService from '../../services/ThemeService';
import LoadingCircle from './../LoadingCircle';
import { SecurityValidation } from '../SecurityValidation/SecurityValidation';
import warning from '../../assets/icons/warning-jumbotron.png';
import ProfileService from '../../services/Profile/ProfileService';
import { CustomModal } from '../CustomModal/CustomModal';

const Profile = (props) => {
  const { t } = props;

  const [userData, setUserData] = useState({});
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirmation, setNewPasswordConfirmation] = useState('');
  const [loading, setLoading] = useState(false);
  const [signatureImage, setSignatureImage] = useState('');
  const [sessionExpired, setSessionExpired] = useState(false);
  const [redirect, setRedirect] = useState(false);
  const [time, setTime] = useState(0);
  const [imageError, setImageError] = useState(false);
  const [imageErrorText, setImageErrorText] = useState('');
  const [dataUpdated, setDataUpdated] = useState(false);
  const [newPasswordError, setNewPasswordError] = useState(false);
  const [newPasswordErrorText, setNewPasswordErrorText] = useState('');
  const [loadingSavingDataProcess, setLoadingSavingDataProcess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorData, setErrorData] = useState('');
  const [errorType, setErrorType] = useState('');
  const [userIsLoggedByAuthCerts, setUserIsLoggedByAuthCerts] = useState(false);
  const [canvasParentWidth, setCanvasParentWidth] = useState(0);
  const [canvasParentHeight] = useState(200);
  const [newSignature, setNewSignature] = useState(false);
  const [windowWidth] = useState(window.innerWidth);
  const [showJumbotron, setShowJumbotron] = useState(true);
  const [tabKey, setTabKey] = useState('file');
  const [fileName, setFileName] = useState("");
  const [isPasswordSecure, setIsPasswordSecure] = useState(false);
  const [loadingSignatureImage, setLoadingSignatureImage] = useState(false);

  const timerRef = useRef(null);
  const sigPadRef = useRef(null);
  const fileInputRef = useRef(null);

  const startTimer = () => {
    timerRef.current = setInterval(() => {
      setTime(prev => prev + 1);
    }, 60000);
  };

  const stopTimer = () => {
    clearInterval(timerRef.current);
  };

  const loadUserData = async () => {
    try {
      setLoading(true);
      const res = await ProfileService.getUserData();
      return res.data;
    } catch (error) {
      if (error.response?.status === 401) {
        setSessionExpired(true);
        startTimer();
      }
      throw error;
    }
  };

  const getSignatureImage = async (legalid) => {
    const res = await ProfileService.getSignatureImage(legalid);
    return res.data.SignatureImage;
  };

  const getUserData = async () => {
    try {
      const data = await loadUserData();
      if (!data) return;

      setUserData(data);
      setLoading(false);
      setLoadingSignatureImage(true);

      try {
        const signature = await getSignatureImage(data.legalid);
        if (signature) {
          setSignatureImage(signature);
        }
      } catch {}
      setLoadingSignatureImage(false);

    } catch {}
  };

  const clearSignatureImage = () => {
    if (fileInputRef.current) fileInputRef.current.value = "";
    setSignatureImage('');
    setFileName("");
  };

  const clearCanvas = () => {
    if (sigPadRef.current) sigPadRef.current.clear();
  };

  const trim = () => {
    if (sigPadRef.current && tabKey === 'canvas') {
      const base64 = sigPadRef.current.toDataURL().split(",")[1];
      setSignatureImage(base64);
      saveUserData(base64);
    } else {
      saveUserData(signatureImage);
    }
  };

  const saveUserData = async (signature) => {
    if (!signature) {
      setImageError(true);
      setImageErrorText(t('profile.image-error'));
      return;
    }

    setLoadingSavingDataProcess(true);
    await handlePasswordChange(signature);
  };

  const handlePasswordChange = async (signature) => {

    if (newPassword && newPassword !== newPasswordConfirmation) {
      setNewPasswordError(true);
      setNewPasswordErrorText(t('profile.password-error'));
      setLoadingSavingDataProcess(false);
      return;
    }

    try {
      await sendSignatureImageToViDSigner(signature);
      await updateUserData(newPassword || null);
    } catch (error) {
      if (error.response?.status === 401) {
        setSessionExpired(true);
        startTimer();
      }
      setLoadingSavingDataProcess(false);
    }
  };

  const sendSignatureImageToViDSigner = async (b64) => {
    const dto = { SignatureImage: b64 };
    return ProfileService.sendSignatureImageToViDSigner(userData.legalid, dto);
  };

  const updateUserData = async (password) => {
    try {
      const updateData = {
        ...userData,
        password,
        signatureimage: signatureImage,
        active: "true"
      };

      await ProfileService.updateUserData(updateData);

      setDataUpdated(true);
      setNewPassword('');
      setNewPasswordConfirmation('');
      setNewPasswordError(false);
      setLoadingSavingDataProcess(false);

      getUserData();
    } catch (error) {
      if (error.response?.status === 401) {
        setSessionExpired(true);
        startTimer();
      } else {
        setErrorType(error.response?.status);
        setErrorData(error.response?.data);
        setShowError(true);
      }
      setLoadingSavingDataProcess(false);
    }
  };

  const discardChanges = () => {
    setFileName("");
    getUserData();
  };

  const resizeCanvas = () => {
    const container = document.getElementsByClassName("profile-container")[0];
    if (container) {
      setCanvasParentWidth(
        container.offsetWidth - (windowWidth > 414 ? 160 : 60)
      );
      
      clearCanvas();
    }
  };

  const doLogOut = () => {
    sessionStorage.clear();
    stopTimer();
    setRedirect(true);
  };

  const handleCloseModal = () => {
    setLoadingSavingDataProcess(false);
    setNewSignature(!newSignature);
  };

  const handleFileSelection = (e) => {
        const imageFile = e.target.files[0];
        
        setFileName(imageFile.name);

        if (typeof imageFile !== "undefined" && imageFile !== "") {
            const reader = new FileReader();
            reader.onload = function () {
                let b64SignatureImage = reader.result;
                b64SignatureImage = b64SignatureImage.substring((b64SignatureImage.indexOf(',') + 1));
                setSignatureImage(b64SignatureImage);
            };
            reader.readAsDataURL(imageFile);
        }
    }

  useEffect(() => {
    ThemeService.setTheme();

    if (sessionStorage.getItem('authCerts')) {
      setUserIsLoggedByAuthCerts(true);
    }

    getUserData();
    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);

    return () => {
      window.removeEventListener("resize", resizeCanvas);
      clearInterval(timerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (redirect) return <Redirect to="/" />;
  if (userIsLoggedByAuthCerts) return <Redirect to="/authcerts" />;

  const getTimeText = () => {
    const language = localStorage.getItem('i18nextLng');
        if (language === 'es') { 
            return `Hace ${time} minutos`; 
        } else if (language === 'en') { 
            return `${time} minutes ago`; 
        } else { 
            return `Fa ${time} minuts`; 
        }
    }

  return (
        <div>
             {dataUpdated && (
                <ToastComponent
                show={dataUpdated}
                autohide
                delay={3000}
                title={t('profile.data-updated')}
                message={t('profile.data-updated-text')}
                variant="success"
                onClose={() => setDataUpdated(false)}
                />
            )}

            {sessionExpired && (
                <ToastComponent
                show={sessionExpired}
                title={t('general.notification')}
                subtitle={getTimeText()}
                variant="session-expired"
                closeButton={false}
                >
                <p>{t('general.session-expired')}</p>
                <Button onClick={doLogOut}>
                    {t('general.accept')}
                </Button>
                </ToastComponent>
            )}
            {showError &&
            <ToastComponent
                onClose={() => setShowError(false)}
                show={showError}
                title={<>{t('general.error')} {errorType}</>}
                message={errorData}
                variant="error"
                closeButton
                zIndex={11}
            />
            }
            <CustomModal
                show={loadingSavingDataProcess}
                handleHide={handleCloseModal}
                title={t('profile.update-process')}
                loading={true}
                closable={false}
            />
            <Header/>
            {showJumbotron &&
            <Jumbotron>
                <MdClose className="close-jumbotron"
                            onClick={() => setShowJumbotron(!showJumbotron)}/>
                <div className="jumbotron-content">
                    <img src={warning} alt="Warning icon"/>
                    <p>
                    {t('profile.update')}. {t('profile.welcome')}
                    </p>
                </div>
            </Jumbotron>
            }
            {loading &&
                <LoadingCircle centerVertically={true} />
            }
            {!loading &&
            <div style={{paddingBottom: '2rem', paddingTop: '2rem'}}>
                <div className="profile-container">
                    {(newPasswordError) &&
                    <div className="error-container">
                        {newPasswordErrorText}
                    </div>
                    }
                    <InputGroup className="mb-3">
                        <InputGroup.Prepend>
                            <InputGroup.Text id="inputGroup-sizing-default">{t('profile.user-id')}</InputGroup.Text>
                        </InputGroup.Prepend>
                        <FormControl
                            aria-label="Default"
                            aria-describedby="inputGroup-sizing-default"
                            readOnly
                            defaultValue={userData.userid || ''}
                        />
                    </InputGroup>
                    <InputGroup className="mb-3">
                        <InputGroup.Prepend>
                            <InputGroup.Text
                                id="inputGroup-sizing-default">{t('profile.user-name')}</InputGroup.Text>
                        </InputGroup.Prepend>
                        <FormControl
                            aria-label="Default"
                            aria-describedby="inputGroup-sizing-default"
                            readOnly
                            defaultValue={userData.username || ''}
                        />
                    </InputGroup>
                    <InputGroup className="mb-3">
                        <InputGroup.Prepend>
                            <InputGroup.Text
                                id="inputGroup-sizing-default">{t('profile.user-legalId')}</InputGroup.Text>
                        </InputGroup.Prepend>
                        <FormControl
                            aria-label="Default"
                            aria-describedby="inputGroup-sizing-default"
                            readOnly
                            defaultValue={userData.legalid || ''}
                        />
                    </InputGroup>
                    <InputGroup className="mb-3">
                        <InputGroup.Prepend>
                            <InputGroup.Text
                                id="inputGroup-sizing-default">{t('profile.user-email')}</InputGroup.Text>
                        </InputGroup.Prepend>
                        <Form.Control
                            type="email"
                            aria-label="Default"
                            aria-describedby="inputGroup-sizing-default"
                            value={userData.email || ''}
                            onChange={e => setUserData({...userData, email: e.target.value})}
                        />
                    </InputGroup>
                    <InputGroup className="mb-3">
                        <InputGroup.Prepend>
                            <InputGroup.Text
                                id="inputGroup-sizing-default">{t('profile.new-password')}</InputGroup.Text>
                        </InputGroup.Prepend>
                        <Form.Control
                            required
                            type="password"
                            aria-label="Default"
                            aria-describedby="inputGroup-sizing-default"
                            value={newPassword}
                            onChange={e => setNewPassword(e.target.value)}
                        />
                    </InputGroup>
                    <InputGroup className="mb-3">
                        <InputGroup.Prepend>
                            <InputGroup.Text
                                id="inputGroup-sizing-default">{t('profile.repeat-password')}</InputGroup.Text>
                        </InputGroup.Prepend>
                        <Form.Control
                            required
                            type="password"
                            aria-label="Default"
                            aria-describedby="inputGroup-sizing-default"
                            value={newPasswordConfirmation}
                            onChange={e =>setNewPasswordConfirmation(e.target.value)}
                        />
                    </InputGroup>
                    {
                        newPassword !== "" && <SecurityValidation password={newPassword} updateIsPasswordSecure={(isSecure) => setIsPasswordSecure(isSecure)} />
                    }
                        <Button
                        type="button"
                        id="newSignatureButton"
                        className="btn btn-primary"
                        onClick={() => setNewSignature(prev => !prev)}
                    >
                        {t('profile.new-signature')}
                    </Button>
                    </div>
                    {newSignature &&
                    <div className="signature-tabs">
                        <Tabs id="new-signature-tab" defaultActiveKey={tabKey} onSelect={k => setTabKey(k)}>
                            <Tab eventKey="file" title={t('profile.from-file')}>
                                <Form.Group
                                    controlId="formFile"
                                    style={{marginTop: 20}}
                                >
                                    <Form.File 
                                        accept="image/png,image/jpg,image/jpeg"
                                        onChange={(e) => handleFileSelection(e)}
                                        ref={fileInputRef}
                                        label={fileName || t('general.form-file-label')}
                                        data-browse={t('general.form-file-browse')}
                                        custom
                                    />
                                    <Form.Text className="text-muted">
                                        {t('profile.select-file')}
                                    </Form.Text>
                                </Form.Group>
                                {(imageError) &&
                                <div className="error-container">
                                    {imageErrorText}
                                </div>
                                }
                                {loadingSignatureImage &&
                                <div style={{marginTop: 20}}>
                                    <PulseLoader
                                        color={'#00bf71'}
                                        loading={true}
                                    />
                                </div>
                                }
                                {!loadingSignatureImage && signatureImage !== '' &&
                                <div className="signature-image-container">
                                    <img style={{maxWidth: '75%'}} alt="signatureImage"
                                            src={`data:image/jpeg;base64,${signatureImage}`}/>
                                    <Button
                                        type="button"
                                        id="profileImageDeleteButton"
                                        className="signature-image reject"
                                        onClick={() => clearSignatureImage()}
                                    >
                                        {t('profile.image-delete')}
                                    </Button>
                                </div>
                                }
                            </Tab>
                            <Tab eventKey="canvas" title={t('profile.from-canvas')}>
                                <div className="signatureCanvas-container">
                                    <SignatureCanvas
                                        backgroundColor={'rgba(255,255,255,1)'}
                                        penColor={'rgb(0,0,255)'}
                                        canvasProps={{
                                            width: canvasParentWidth,
                                            height: canvasParentHeight,
                                            className: 'sigCanvas'
                                        }}
                                        clearOnResize={true}
                                        ref={sigPadRef}
                                    />
                                </div>
                                <div className="button-container">
                                    <Button
                                        type="button"
                                        id="signatureDeleteButton"
                                        className="btn reject"
                                        onClick={clearCanvas}
                                        style={{marginLeft: '10px'}}
                                    >
                                        {t('profile.signature-delete')}
                                    </Button>
                                </div>
                            </Tab>
                        </Tabs>
                    </div>
                    }
                <div className="button-container">
                    <Button
                        disabled={(newPassword !== "" && !isPasswordSecure)}
                        type="submit"
                        id="submitButtonSaveUserData"
                        className="btn btn-primary"
                        onClick={trim}
                    >
                        {t('profile.save')}
                    </Button>
                    <Button
                        type="submit"
                        id="submitButtonDiscardChanges"
                        className="btn reject"
                        onClick={() => discardChanges()}
                        style={{marginLeft:20}}
                    >
                        {t('profile.discard')}
                    </Button>
                </div>
            </div>
            }
        </div>
    );
};

export default withTranslation()(Profile);
